<template>
  <div class="formulate-input-element">
    <!-- Debug: mostrar se o contexto está sendo passado -->
    <div
      v-if="!context"
      class="alert alert-danger"
    >
      Contexto não encontrado!
    </div>

    <div v-else>
      <!-- Toggle Button -->
      <div class="editor-controls">
        <button
          type="button"
          class="toggle-button"
          :class="{ active: isCodeMode }"
          :disabled="context.disabled"
          @click="toggleMode"
        >
          {{ isCodeMode ? 'Modo Visual' : 'Modo Código' }}
        </button>
        <button
          v-if="isCodeMode"
          type="button"
          class="format-button"
          :disabled="context.disabled"
          title="Formatar HTML"
          @click="formatCode"
        >
          Formatar
        </button>
      </div>

      <!-- Visual Mode (Vue Editor) -->
      <vue-editor
        v-if="!isCodeMode"
        v-model="context.model"
        :placeholder="context.placeholder || 'Digite o conteúdo...'"
        :editor-toolbar="editorToolbar"
        :disabled="context.disabled"
        @blur="context.blurHandler || blur"
        @focus="context.focusHandler || focus"
      />

      <!-- Code Mode (Textarea) -->
      <textarea
        v-else
        v-model="context.model"
        :placeholder="context.placeholder || 'Digite o HTML...'"
        :disabled="context.disabled"
        class="code-textarea"
        @blur="context.blurHandler || blur"
        @focus="context.focusHandler || focus"
      />
    </div>
  </div>
</template>

<script>
import { VueEditor } from 'vue2-editor'

// Importar estilos do Quill
import 'quill/dist/quill.core.css'
import 'quill/dist/quill.snow.css'

export default {
  name: 'FormulateInputRichEditor',
  components: {
    VueEditor,
  },
  props: {
    context: {
      type: Object,
      required: true,
    },
  },
  data() {
    return {
      isCodeMode: false,
    }
  },
  computed: {
    editorToolbar() {
      return (
        this.context.toolbar || [
          ['bold', 'italic', 'underline'],
          [{ align: '' }, { align: 'center' }, { align: 'right' }, { align: 'justify' }],
          [{ list: 'ordered' }, { list: 'bullet' }],
          ['link'],
          ['clean'],
          ["image", "code-block"],
        ]
      )
    },
  },
  methods: {
    toggleMode() {
      this.isCodeMode = !this.isCodeMode
      console.log(this.isCodeMode ? 'Modo Código ativado' : 'Modo Visual ativado')
    },
    async formatCode() {
      try {
        // Tentar usar prettier se disponível
        if (window.prettier && window.prettierPlugins) {
          const formatted = await window.prettier.format(this.context.model, {
            parser: 'html',
            plugins: window.prettierPlugins,
          })
          this.context.model = formatted
          console.log('HTML formatado com sucesso')
        } else {
          // Fallback: formatação básica usando regex e indentação
          this.context.model = this.formatHtmlBasic(this.context.model)
          console.log('HTML formatado com formatação básica')
        }
      } catch (error) {
        console.error('Erro ao formatar HTML:', error)
        // Mesmo se houver erro, tentar formatação básica
        this.context.model = this.formatHtmlBasic(this.context.model)
      }
    },
    formatHtmlBasic(html) {
      if (!html) return html

      let formatted = html
      let indent = 0
      const indentStr = '  '

      // Quebra linhas em tags
      formatted = formatted.replace(/></g, '>\n<')

      // Processa cada linha
      const lines = formatted.split('\n').map(line => {
        // eslint-disable-next-line no-param-reassign
        line = line.trim()
        if (!line) return line

        // Verifica se é tag de fechamento
        if (line.startsWith('</')) {
          indent = Math.max(0, indent - 1)
        }

        const indented = indentStr.repeat(indent) + line

        // Verifica se é tag de abertura (não auto-fechada, não comentário, não closing)
        if (line.startsWith('<') && !line.startsWith('</') && !line.endsWith('/>')) {
          if (!line.match(/<(area|base|br|col|embed|hr|img|input|link|meta|param|source|track|wbr)/i)) {
            // eslint-disable-next-line no-plusplus
            indent++
          }
        }

        return indented
      })

      return lines.filter(line => line.trim()).join('\n')
    },
    focus() {},
    blur() {},
  },
  mounted() {
    // Debug: verificar se o context está sendo passado corretamente
    console.log('RichEditor mounted with context:', this.context)
    console.log('VueEditor component:', this.$options.components.VueEditor)
  },
}
</script>

<style scoped>
.formulate-input-element {
  width: 100%;
}

.alert {
  padding: 10px;
  margin: 10px 0;
  border-radius: 4px;
}

.alert-danger {
  background-color: #f8d7da;
  border: 1px solid #f5c6cb;
  color: #721c24;
}

/* Controls section */
.editor-controls {
  margin-bottom: 10px;
  display: flex;
  gap: 8px;
  flex-wrap: wrap;
}

/* Toggle Button */
.toggle-button {
  padding: 8px 16px;
  background-color: #f8f9fa;
  border: 1px solid #ccc;
  border-radius: 4px;
  cursor: pointer;
  font-size: 14px;
  font-weight: 500;
  color: #333;
  transition: all 0.3s ease;
}

.toggle-button:hover:not(:disabled) {
  background-color: #e9ecef;
  border-color: #adb5bd;
}

.toggle-button.active {
  background-color: #0d6efd;
  border-color: #0d6efd;
  color: white;
}

.toggle-button:disabled {
  opacity: 0.6;
  cursor: not-allowed;
}

/* Format Button */
.format-button {
  padding: 8px 16px;
  background-color: #28a745;
  border: 1px solid #28a745;
  border-radius: 4px;
  cursor: pointer;
  font-size: 14px;
  font-weight: 500;
  color: white;
  transition: all 0.3s ease;
}

.format-button:hover:not(:disabled) {
  background-color: #218838;
  border-color: #1e7e34;
}

.format-button:active:not(:disabled) {
  background-color: #1e7e34;
  transform: scale(0.98);
}

.format-button:disabled {
  opacity: 0.6;
  cursor: not-allowed;
}

/* Code Textarea */
.code-textarea {
  width: 100%;
  min-height: 300px;
  padding: 12px;
  font-family: 'Courier New', Courier, monospace;
  font-size: 13px;
  border: 1px solid #ccc;
  border-radius: 4px;
  resize: vertical;
  background-color: white;
  color: #333;
}

.code-textarea:focus {
  border-color: #0d6efd;
  outline: none;
  box-shadow: 0 0 0 0.2rem rgba(13, 110, 253, 0.25);
}

.code-textarea:disabled {
  background-color: #e9ecef;
  color: #6c757d;
  cursor: not-allowed;
}

/* Customização do editor */
:deep(.ql-editor) {
  min-height: 150px !important;
  font-family: inherit;
  background-color: white !important;
}

:deep(.ql-toolbar) {
  border: 1px solid #ccc !important;
  background-color: #f8f9fa !important;
}

:deep(.ql-container) {
  border: 1px solid #ccc !important;
  background-color: white !important;
}
</style>
